import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAllProvinceOptions } from '@anytag/shared-hooks';
import { SelectorField, SingleSelectorFieldProps, MultipleSelectorFieldProps } from './selector-field';

interface BaseProvinceFieldProps {
  countryId?: string;
}

export type SingleProvinceFieldProps<T extends string> = SingleSelectorFieldProps<T> & BaseProvinceFieldProps;
export type MultipleProvinceFieldProps<T extends string> = MultipleSelectorFieldProps<T> & BaseProvinceFieldProps;
export type ProvinceFieldProps<T extends string> = SingleProvinceFieldProps<T> | MultipleProvinceFieldProps<T>;

// Function overloads for proper type inference based on `multiple` prop
export function ProvinceField<T extends string>(props: MultipleProvinceFieldProps<T>): React.ReactElement;
export function ProvinceField<T extends string>(props: SingleProvinceFieldProps<T>): React.ReactElement;
export function ProvinceField<T extends string>({ title, disabled, countryId, ...restProps }: ProvinceFieldProps<T>) {
  const { t } = useTranslation();
  const { allProvinceOptions } = useAllProvinceOptions({
    skip: !countryId,
    fetchPolicy: 'cache-and-network',
    variables: { countryId: countryId || '' }
  });

  return (
    <SelectorField<T>
      {...(restProps as SingleSelectorFieldProps<T>)}
      options={allProvinceOptions}
      title={title ?? t('Selector.Province')}
      disabled={disabled || !countryId || allProvinceOptions.length === 0}
    />
  );
}
